import { me } from '@connected/api-client/data/auth/me';
import { isAuthenticated } from '@connected/api-client/lib/authentication';
import { checkRouteGuard } from '@connected/core';
import { createFileRoute } from '@tanstack/react-router';

import type { ElektraConditionContext } from '@/config/conditions';

import { elektraConditions, elektraRouteGuard, getRedirectUrlFromResult } from '@/config/routing';

/**
 * Root route for elektra-app
 *
 * The root path "/" is not an allowed route in elektra-app (only /projects/{uuid}/** is allowed),
 * so this route uses the route guard to redirect to portaal with the appropriate fallback.
 */
export const Route = createFileRoute('/')({
    beforeLoad: async ({ context, location }) => {
        // Try to get current user - may be null if not authenticated
        let currentUser = null;
        const isUserAuthenticated = isAuthenticated();

        if (isUserAuthenticated) {
            try {
                currentUser = await context.queryClient.ensureQueryData(me.queryOptions());
            }
            catch {
                // Failed to fetch user - treat as not authenticated
            }
        }

        // Build the condition context for route guard evaluation
        const conditionContext: ElektraConditionContext = {
            currentUser,
            isAuthenticated: isUserAuthenticated && currentUser !== null,
            currentPath: location.pathname,
            // Route guards don't need project-specific context
            hasInspectionKind: () => false,
            enabledElectricalProjectPriceQuote: false,
            enabledElectricalProjectPlanning: false,
            enableExtendedRiso: false,
            enableImpedanceMeasurementsPage: false,
            hasResidualCurrentCircuitBreakerDevices: false,
        };

        const result = checkRouteGuard({
            config: elektraRouteGuard,
            context: conditionContext,
            conditions: elektraConditions,
        });

        if (!result.allowed) {
            const redirectUrl = getRedirectUrlFromResult(result);
            window.location.href = redirectUrl;
        }
    },
});
